import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc, deleteDoc, Timestamp } from 'firebase/firestore';
import { WORKOUT_TYPES, SPORT_TYPES, PRIORITY_LEVELS, getWorkoutTypeLabel, getSportTypeLabel } from '../utils/trainingTypes';
import { estimateWorkoutTSS } from '../utils/tssCalculator';

const overlayBaseStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20000,
};

const modalStyle = {
  backgroundColor: '#0f0f0f',
  color: '#e0e0e0',
  padding: '24px',
  borderRadius: '12px',
  border: '1px solid #2a2a2a',
  width: '92vw',
  maxWidth: '700px',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)',
};

const btnStyle = {
  background: 'transparent',
  border: '1px solid #2a2a2a',
  color: '#e0e0e0',
  borderRadius: '8px',
  padding: '10px 16px',
  cursor: 'pointer',
  fontWeight: 500,
};

const primaryBtnStyle = {
  ...btnStyle,
  background: 'var(--brand-accent-gradient)',
  border: 'none',
  color: '#fff',
};

const dangerBtnStyle = {
  ...btnStyle,
  background: '#dc3545',
  border: 'none',
  color: '#fff',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  backgroundColor: '#1a1a1a',
  border: '1px solid #2a2a2a',
  borderRadius: '8px',
  color: '#e0e0e0',
  fontSize: '14px',
  boxSizing: 'border-box',
};

export default function WorkoutEditorModal({ isOpen, onClose, userId, workout, planId, mode = 'create' }) {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('endurance');
  const [sport, setSport] = useState('cycling');
  const [priority, setPriority] = useState('p2');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [duration, setDuration] = useState(''); // minutes
  const [intensityZone, setIntensityZone] = useState(2);
  const [description, setDescription] = useState('');
  const [targetTSS, setTargetTSS] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    if (workout && mode === 'edit') {
      // Load existing workout data
      setName(workout.name || '');
      setType(workout.type || 'endurance');
      setSport(workout.sport || 'cycling');
      setPriority(workout.priority || 'p2');
      setDescription(workout.description || '');
      
      if (workout.scheduledDate) {
        let date;
        if (workout.scheduledDate?.toDate) {
          date = workout.scheduledDate.toDate();
        } else if (workout.scheduledDate instanceof Date) {
          date = workout.scheduledDate;
        } else if (typeof workout.scheduledDate === 'number') {
          date = new Date(workout.scheduledDate);
        } else {
          date = new Date(workout.scheduledDate);
        }
        setScheduledDate(date.toISOString().split('T')[0]);
        setScheduledTime(workout.scheduledTime || '');
      }

      if (workout.targetDuration) {
        setDuration(String(Math.round(workout.targetDuration / 60)));
      }

      if (workout.targetIntensity?.zone) {
        setIntensityZone(workout.targetIntensity.zone);
      }

      if (workout.targetTSS) {
        setTargetTSS(workout.targetTSS);
      }
    } else {
      // Create mode - set defaults
      const today = new Date();
      today.setDate(today.getDate() + 1);
      setScheduledDate(today.toISOString().split('T')[0]);
      setScheduledTime('10:00');
      setDuration('60');
    }
  }, [isOpen, workout, mode]);

  useEffect(() => {
    if (duration && intensityZone) {
      const estimatedTSS = estimateWorkoutTSS(parseFloat(duration) * 60, intensityZone);
      setTargetTSS(Math.round(estimatedTSS * 10) / 10);
    }
  }, [duration, intensityZone]);

  function resetForm() {
    setName('');
    setType('endurance');
    setSport('cycling');
    setPriority('p2');
    setScheduledDate('');
    setScheduledTime('');
    setDuration('');
    setIntensityZone(2);
    setDescription('');
    setTargetTSS(0);
  }

  async function handleSave() {
    if (!userId || !name.trim() || !scheduledDate || !duration) {
      alert('Please fill in required fields (name, date, duration)');
      return;
    }

    try {
      setSaving(true);

      const date = new Date(scheduledDate);
      if (scheduledTime) {
        const [hours, minutes] = scheduledTime.split(':').map(Number);
        date.setHours(hours, minutes, 0, 0);
      }

      const workoutData = {
        userId,
        name: name.trim(),
        type,
        sport,
        priority,
        description: description.trim() || '',
        scheduledDate: Timestamp.fromDate(date),
        scheduledTime: scheduledTime || null,
        targetDuration: parseFloat(duration) * 60, // Convert to seconds
        targetTSS,
        targetIntensity: {
          zone: intensityZone,
        },
        status: 'planned',
        structure: {
          type: 'simple',
          simple: {
            duration: parseFloat(duration) * 60,
            intensity: intensityZone,
          }
        },
        updatedAt: Timestamp.now(),
      };

      if (planId) {
        workoutData.planId = planId;
      }

      if (mode === 'edit' && workout?.id) {
        await updateDoc(doc(db, 'plannedWorkouts', workout.id), workoutData);
        alert('Workout updated successfully!');
      } else {
        await addDoc(collection(db, 'plannedWorkouts'), {
          ...workoutData,
          autoGenerated: false,
          createdAt: Timestamp.now(),
        });
        alert('Workout created successfully!');
      }

      onClose(true); // Pass true to indicate refresh needed
    } catch (error) {
      console.error('Error saving workout:', error);
      alert('Failed to save workout. Please check the console for details.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!workout?.id) return;
    
    const confirm = window.confirm('Are you sure you want to delete this workout?');
    if (!confirm) return;

    try {
      setSaving(true);
      await deleteDoc(doc(db, 'plannedWorkouts', workout.id));
      alert('Workout deleted successfully!');
      onClose(true); // Pass true to indicate refresh needed
    } catch (error) {
      console.error('Error deleting workout:', error);
      alert('Failed to delete workout. Please check the console for details.');
    } finally {
      setSaving(false);
    }
  }

  async function handleComplete() {
    if (!workout?.id) return;

    try {
      setSaving(true);
      await updateDoc(doc(db, 'plannedWorkouts', workout.id), {
        status: 'completed',
        completedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      alert('Workout marked as completed!');
      onClose(true); // Pass true to indicate refresh needed
    } catch (error) {
      console.error('Error completing workout:', error);
      alert('Failed to mark workout as completed.');
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  const howToText = (description || '').trim();

  return (
    <div style={overlayBaseStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#e0e0e0' }}>
            {mode === 'edit' ? 'Edit Workout' : 'Create Workout'}
          </h2>
          <button
            onClick={onClose}
            style={{
              ...btnStyle,
              padding: '8px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaTimes />
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Workout Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Long Endurance Ride"
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              style={inputStyle}
            >
              {WORKOUT_TYPES.map((t) => (
                <option key={t} value={t}>{getWorkoutTypeLabel(t)}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Sport</label>
            <select
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              style={inputStyle}
            >
              {SPORT_TYPES.map((s) => (
                <option key={s} value={s}>{getSportTypeLabel(s)}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Priority</label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            style={inputStyle}
          >
            {PRIORITY_LEVELS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Date *</label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Time</label>
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Duration (minutes) *</label>
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              min="1"
              max="600"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Intensity Zone (1-5)</label>
            <input
              type="number"
              value={intensityZone}
              onChange={(e) => setIntensityZone(Number(e.target.value))}
              min="1"
              max="5"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#1a1a1a', borderRadius: '8px' }}>
          <div style={{ fontSize: '12px', color: '#9aa0a6', marginBottom: '4px' }}>Estimated TSS:</div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: 'var(--brand-primary)' }}>{targetTSS}</div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Description</label>
          {howToText ? (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#151515',
                border: '1px solid #2a2a2a',
                borderRadius: '10px',
                marginBottom: '10px',
              }}
            >
              <div style={{ fontSize: '12px', color: '#9aa0a6', marginBottom: '6px' }}>
                How to do this workout
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', color: '#e0e0e0', lineHeight: 1.5 }}>
                {howToText}
              </div>
            </div>
          ) : null}
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Additional notes about this workout..."
            style={{
              ...inputStyle,
              minHeight: '80px',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          {mode === 'edit' && workout?.status === 'planned' && (
            <button onClick={handleComplete} style={primaryBtnStyle} disabled={saving}>
              Mark Complete
            </button>
          )}
          {mode === 'edit' && (
            <button onClick={handleDelete} style={dangerBtnStyle} disabled={saving}>
              Delete
            </button>
          )}
          <button onClick={onClose} style={btnStyle} disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} style={primaryBtnStyle} disabled={saving || !name.trim() || !scheduledDate || !duration}>
            {saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
